import type { ReportGranularity } from "./types";

/** A `YYYY-MM-DD` read as that calendar day wherever the browser is. */
function calendarDay(period: string): Date {
    const [year, month, day] = period.split("-").map(Number);
    return new Date(year, (month ?? 1) - 1, day ?? 1);
}

const AXIS_FORMAT: Record<ReportGranularity, Intl.DateTimeFormatOptions> = {
    day: { day: "numeric", month: "short" },
    month: { month: "short", year: "2-digit" },
    year: { year: "numeric" },
};

const FULL_FORMAT: Record<ReportGranularity, Intl.DateTimeFormatOptions> = {
    day: { day: "numeric", month: "long", year: "numeric" },
    month: { month: "long", year: "numeric" },
    year: { year: "numeric" },
};

/** Short enough for an axis tick: "3 ago", "ago 26", "2026". */
export function formatPeriodShort(
    period: string,
    granularity: ReportGranularity,
    locale: string
): string {
    return calendarDay(period).toLocaleDateString(locale, AXIS_FORMAT[granularity]);
}

/** The whole period, for a tooltip or a table: "3 de agosto de 2026". */
export function formatPeriodFull(
    period: string,
    granularity: ReportGranularity,
    locale: string
): string {
    return calendarDay(period).toLocaleDateString(locale, FULL_FORMAT[granularity]);
}

/** Money arrives as a decimal string; charts and sums want a number. */
export function toAmount(value: string | number): number {
    const amount = typeof value === "number" ? value : Number.parseFloat(value);
    return Number.isFinite(amount) ? amount : 0;
}

/** Every payment in Alchely is in euros. */
export function formatMoney(value: number, locale: string): string {
    return new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(value);
}

/** Whole euros, for an axis tick, where cents are only noise. */
export function formatMoneyRounded(value: number, locale: string): string {
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    }).format(value);
}

export function formatCount(value: number, locale: string): string {
    return new Intl.NumberFormat(locale).format(value);
}
